/* =========================================================
   ADWA HERMI — Page Modal
   Intercepts clicks on links that point to the four "stay
   in place" pages and shows them in a popup instead of
   navigating away. Client content is still edited via the
   Shopify admin Pages UI.
   ========================================================= */

(function () {
  'use strict';

  // Page handles that should open as a modal instead of navigating.
  // If Adwa adds new pages later, extend this list.
  var MODAL_HANDLES = new Set([
    'gift-receiving',
    'about',
    'become-a-member'
  ]);

  // Page handles that should open an external URL in a new tab
  // instead of navigating to the page itself. Resolved at click time.
  var EXTERNAL_HANDLES = {
    'book-an-appointment': function () {
      return (window.AdwaSettings && window.AdwaSettings.calendlyUrl) ||
        'https://calendly.com/adwa-adwahermi/30min';
    }
  };

  var overlay;
  var modalEl;
  var titleEl;
  var contentEl;
  var closeBtn;
  var lastFocused;
  var cache = new Map();

  function buildOverlay() {
    overlay = document.createElement('div');
    overlay.className = 'adwa-modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('aria-labelledby', 'adwa-modal-title');
    overlay.innerHTML = [
      '<div class="adwa-modal" tabindex="-1">',
      '  <button class="adwa-modal__close" aria-label="Close" type="button">',
      '    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">',
      '      <line x1="5" y1="5" x2="19" y2="19"/>',
      '      <line x1="19" y1="5" x2="5" y2="19"/>',
      '    </svg>',
      '  </button>',
      '  <h2 class="adwa-modal__title" id="adwa-modal-title"></h2>',
      '  <div class="adwa-modal__content rte"></div>',
      '</div>'
    ].join('');
    document.body.appendChild(overlay);

    modalEl = overlay.querySelector('.adwa-modal');
    titleEl = overlay.querySelector('.adwa-modal__title');
    contentEl = overlay.querySelector('.adwa-modal__content');
    closeBtn = overlay.querySelector('.adwa-modal__close');

    closeBtn.addEventListener('click', closeModal);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeModal();
    });
  }

  function extractHandle(href) {
    if (!href) return null;
    try {
      var u = new URL(href, window.location.origin);
      if (u.origin !== window.location.origin) return null;
      var m = u.pathname.match(/^\/pages\/([^\/?#]+)/);
      return m ? m[1] : null;
    } catch (e) {
      return null;
    }
  }

  function fetchPage(handle) {
    if (cache.has(handle)) return Promise.resolve(cache.get(handle));
    return fetch('/pages/' + encodeURIComponent(handle) + '?view=modal', {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var wrap = doc.querySelector('[data-adwa-modal-page]');
        var data;
        if (wrap) {
          var t = wrap.querySelector('.adwa-modal-page__title');
          var c = wrap.querySelector('.adwa-modal-page__content');
          data = {
            title: t ? t.textContent.trim() : '',
            content: c ? c.innerHTML : ''
          };
        } else {
          // Fallback: if the site serves the full template (e.g. ?view=modal
          // not yet published), try to pull the page title + content anyway.
          var titleNode = doc.querySelector('h1');
          data = {
            title: titleNode ? titleNode.textContent.trim() : '',
            content: doc.body ? doc.body.innerHTML : ''
          };
        }
        cache.set(handle, data);
        return data;
      });
  }

  // Pages whose content collapses behind a Read More toggle
  // when the rendered text is taller than the threshold.
  var COLLAPSIBLE_HANDLES = new Set(['about']);
  var COLLAPSE_THRESHOLD_PX = 280;

  function maybeApplyReadMore(handle) {
    contentEl.classList.remove('is-collapsible', 'is-expanded');
    var existingBtn = modalEl.querySelector('.adwa-modal__readmore');
    if (existingBtn) existingBtn.remove();

    if (!COLLAPSIBLE_HANDLES.has(handle)) return;

    // Wrap the content in an inner div so we can clip with mask-image
    // without losing the original markup.
    var inner = document.createElement('div');
    inner.className = 'adwa-modal__content-inner';
    while (contentEl.firstChild) inner.appendChild(contentEl.firstChild);
    contentEl.appendChild(inner);

    // Defer measurement until after layout settles
    requestAnimationFrame(function () {
      if (inner.scrollHeight <= COLLAPSE_THRESHOLD_PX + 40) return;

      contentEl.classList.add('is-collapsible');

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'adwa-modal__readmore';
      btn.textContent = 'Read More';
      btn.setAttribute('aria-expanded', 'false');

      btn.addEventListener('click', function () {
        var expanded = contentEl.classList.toggle('is-expanded');
        contentEl.classList.toggle('is-collapsible', !expanded);
        btn.textContent = expanded ? 'Read Less' : 'Read More';
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });

      contentEl.parentNode.insertBefore(btn, contentEl.nextSibling);
    });
  }

  function openModal(handle, triggerEl) {
    if (!overlay) buildOverlay();
    lastFocused = triggerEl || document.activeElement;

    titleEl.textContent = '';
    contentEl.innerHTML = '<div class="adwa-modal__loading">Loading…</div>';

    document.body.classList.add('adwa-modal-open');
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    modalEl.scrollTop = 0;

    fetchPage(handle)
      .then(function (data) {
        titleEl.textContent = data.title;
        contentEl.innerHTML = data.content;
        executeInlineScripts(contentEl);
        maybeApplyReadMore(handle);
        closeBtn.focus();
      })
      .catch(function () {
        titleEl.textContent = '';
        contentEl.innerHTML = '<p>We couldn’t load this page. Please try again.</p>';
      });
  }

  // innerHTML doesn't execute <script> tags. Re-create them so embeds
  // (Calendly, etc.) actually load when the modal is opened.
  function executeInlineScripts(root) {
    var scripts = root.querySelectorAll('script');
    scripts.forEach(function (oldScript) {
      var newScript = document.createElement('script');
      for (var i = 0; i < oldScript.attributes.length; i++) {
        var attr = oldScript.attributes[i];
        newScript.setAttribute(attr.name, attr.value);
      }
      if (oldScript.textContent) newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  function closeModal() {
    if (!overlay || !overlay.classList.contains('is-open')) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('adwa-modal-open');
    if (lastFocused && typeof lastFocused.focus === 'function') {
      try { lastFocused.focus(); } catch (e) { /* noop */ }
    }
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && overlay && overlay.classList.contains('is-open')) {
      closeModal();
    }
  });

  document.addEventListener('click', function (e) {
    var a = e.target.closest ? e.target.closest('a') : null;
    if (!a) return;
    if (a.hasAttribute('data-adwa-no-modal')) return;
    if (a.target && a.target !== '' && a.target !== '_self') return;
    if (e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

    var handle = extractHandle(a.getAttribute('href') || '');
    if (!handle) return;

    // External redirect (e.g. Calendly for Book an Appointment) — open in new tab
    if (EXTERNAL_HANDLES[handle]) {
      e.preventDefault();
      var url = EXTERNAL_HANDLES[handle]();
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    if (!MODAL_HANDLES.has(handle)) return;
    e.preventDefault();
    openModal(handle, a);
  }, false);
})();
